/**
 * "Pick up your flashing camera" — intermediate confirmation screen shown
 * after a successful Checkout from the NeedCameraPage. Displays the dock
 * image, the employee who was just assigned, and an auto-returning
 * countdown that sends the kiosk back to the Camera Checkout (2-options)
 * screen for the same org / site so the next employee can start straight
 * away — no trip through the portal Checkouts list.
 *
 * Query params (all optional):
 *   - checkoutId  : kiosk id we launched from (forwarded back to /checkout/camera)
 *   - org / site / orgId / siteId : tenant context (forwarded back)
 *   - name        : display name to show (e.g. "Alice Wilson")
 *   - employeeId  : customer employee id (shown in parentheses)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/camera-checkout-page`;

const COUNTDOWN_SECONDS = 15;

export default function PickCameraPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const checkoutId = params.get('checkoutId');
  const orgName    = params.get('org')   || '';
  const siteName   = params.get('site')  || '';
  const orgId      = params.get('orgId')  || '';
  const siteId     = params.get('siteId') || '';
  const name = params.get('name') || 'Alice Wilson';
  const employeeId = params.get('employeeId') || '123567';

  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  // Build the shared tenant-context query string (checkoutId + org/site).
  const buildQs = () => {
    const qp = new URLSearchParams();
    if (checkoutId) qp.set('checkoutId', checkoutId);
    if (orgName)    qp.set('org',    orgName);
    if (siteName)   qp.set('site',   siteName);
    if (orgId)      qp.set('orgId',  orgId);
    if (siteId)     qp.set('siteId', siteId);
    const s = qp.toString();
    return s ? `?${s}` : '';
  };

  const goHome = () => {
    // After a successful camera hand-off we return to the Camera Checkout
    // (2-options) screen so the next employee can immediately start a new
    // checkout, without having to re-pick a kiosk from the portal list.
    navigate(`/checkout/camera${buildQs()}`);
  };

  const goBack = () => {
    navigate(`/checkout/need-camera${buildQs()}`);
  };

  useEffect(() => {
    if (secondsLeft <= 0) {
      goHome();
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  return (
    <div className="ck-page">
      <header className="ck-header nc-header">
        <div className="nc-header-left">
          <button type="button" className="nc-back-btn" title="Back" onClick={goBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="cc-brand">
            {orgName || 'Organization'}
            {siteName && <span className="cc-brand-site">/{siteName}</span>}
          </div>
        </div>
        <div className="cc-header-actions">
          <button type="button" className="cc-header-btn" title="Exit" onClick={goHome}>
            <img src={`${ICON_BASE}/signout.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn" title="Help">
            <img src={`${ICON_BASE}/questionmark.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn cc-header-lang">
            English <i className="fas fa-chevron-down"></i>
          </button>
        </div>
      </header>

      <main className="pc-main">
        <img
          src={`${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/pick-flashing-camera.png`}
          alt="Flashing camera in dock"
          className="pc-image"
        />

        <h1 className="pc-title">Pick up your flashing camera</h1>
        <p className="pc-subtitle">{name} (#{employeeId})</p>

        <p className="pc-countdown">
          Returning to home screen in{' '}
          <span className="pc-countdown-n">{secondsLeft} seconds</span>
        </p>
      </main>
    </div>
  );
}

